// ============================================================================
// DATA CONSTANTS - Sustainability Assessment Platform
// Created by Salvador Janeiro
// ============================================================================

// All emission factors, water factors, plastic factors, and question banks
// Sources: EPA, IPCC, Water Footprint Network, UN Environment

// ============================================================================
// EMISSION FACTORS
// ============================================================================

export const EMISSION_FACTORS = {
  // Transportation (kg CO2e per km)
  transport: {
    walking: 0,
    cycling: 0,
    publicTransit: 0.041,
    hybridCar: 0.109,
    gasolineCar: 0.171,
    dieselCar: 0.171,
  },

  // Flights (kg CO2e per km)
  flights: {
    short: 0.255,    // <1500km
    medium: 0.195,   // 1500-4000km
    long: 0.150,     // >4000km
  },

  // Energy (kg CO2e per kWh)
  electricity: {
    renewable: 0,
    grid: 0.233,           // EU average
    naturalGas: 0.185,
    fossilFuels: 0.850,
  },

  // Diet (kg CO2e per day)
  diet: {
    vegan: 1.5,
    vegetarian: 2.5,
    mixedLowMeat: 3.8,
    meatHeavy: 5.2,
  },

  // Waste (kg CO2e per kg)
  waste: {
    trash: 0.45,
    recycling: 0.02,
  },

  // Other
  digital: 0.06,  // kg CO2e per hour online
  clothing: 10,   // kg CO2e per item (average)
  electronics: 70, // kg CO2e per device (average)
};

// ============================================================================
// WATER FOOTPRINT FACTORS
// ============================================================================

export const WATER_FACTORS = {
  // Food production (liters per kg)
  food: {
    beef: 15000,
    pork: 6000,
    chicken: 4300,
    cheese: 3200,
    eggs: 3300,
    rice: 2500,
    vegetables: 322,
    fruits: 962,
    plantProtein: 1250,
  },

  // Clothing (liters per item)
  clothing: {
    cottonShirt: 2700,
    jeans: 7600,
    syntheticShirt: 1800,
  },

  // Home water use (liters per day)
  homeUse: {
    longShower: 80,      // 10 min
    shortShower: 40,     // 5 min
    efficient: 25,       // 3 min
  },

  // Diet water footprint (liters per year)
  dietAnnual: {
    vegan: 1200000,      // 1.2 million liters
    vegetarian: 1800000,
    mixedLowMeat: 2500000,
    meatHeavy: 3800000,
  },
};

// ============================================================================
// PLASTIC WASTE FACTORS
// ============================================================================

export const PLASTIC_FACTORS = {
  // Based on usage patterns (kg per year)
  bottles: {
    daily: 15,
    weekly: 7,
    rarely: 1,
  },
  bags: {
    often: 8,
    sometimes: 3,
    rarely: 0.5,
  },
  baselineAverage: 28, // kg per person per year globally
};

// ============================================================================
// BENCHMARKS & TARGETS
// ============================================================================

export const BENCHMARKS = {
  // Carbon footprint (tons CO2e per year)
  carbon: {
    unTarget: 2.5,        // UN 1.5°C target per person
    euAverage: 8.33,      // EU average per person
    globalAverage: 4.8,   // Global average
  },

  // Water footprint (m³ per year)
  water: {
    targetEfficient: 1500000, // 1.5 million liters
    globalAverage: 3000000,   // 3 million liters
  },

  // Plastic waste (kg per year)
  plastic: {
    globalAverage: 28,
    targetLow: 10,
  },
};

// ============================================================================
// INDIVIDUAL QUESTIONS
// ============================================================================

export const INDIVIDUAL_QUESTIONS = [
  // TRANSPORTATION
  {
    id: 'commute',
    category: 'Transportation',
    question: 'How do you commute to work or school on most days?',
    info: 'Transportation is one of the largest contributors to personal carbon footprints.',
    type: 'single',
    options: [
      { value: 'walking', label: 'Walking or Cycling', icon: '🚶' },
      { value: 'publicTransit', label: 'Public Transit (bus, train, metro)', icon: '🚌' },
      { value: 'hybridCar', label: 'Hybrid or Electric Car', icon: '🔋' },
      { value: 'gasolineCar', label: 'Gasoline or Diesel Car', icon: '🚗' },
    ],
  },
  {
    id: 'commuteDistance',
    category: 'Transportation',
    question: 'What is your average daily commute distance (round trip)?',
    info: 'Distance traveled significantly impacts your carbon footprint.',
    type: 'single',
    options: [
      { value: '0', label: 'Work from home / No commute', icon: '🏠' },
      { value: '10', label: 'Less than 10 km', icon: '📍' },
      { value: '30', label: '10-30 km', icon: '📍' },
      { value: '60', label: '30-60 km', icon: '📍' },
      { value: '100', label: 'More than 60 km', icon: '📍' },
    ],
  },
  {
    id: 'flights',
    category: 'Transportation',
    question: 'How often do you fly in a typical year?',
    info: 'One transatlantic flight can emit approximately 1.6 tons of CO₂.',
    type: 'single',
    options: [
      { value: 'never', label: 'Never or very rarely', icon: '🏠' },
      { value: 'short1-2', label: '1-2 short-haul flights (<1500km)', icon: '✈️' },
      { value: 'several', label: 'Several flights (3-5 per year)', icon: '✈️' },
      { value: 'frequent', label: 'Frequent flyer (6+ flights)', icon: '✈️' },
    ],
  },

  // HOME ENERGY
  {
    id: 'energySource',
    category: 'Home Energy',
    question: "What is your home's primary energy source?",
    info: 'Renewable energy can reduce your carbon footprint to near zero for electricity.',
    type: 'single',
    options: [
      { value: 'renewable', label: 'Renewable electricity (solar, wind)', icon: '☀️' },
      { value: 'grid', label: 'Grid electricity (mixed sources)', icon: '⚡' },
      { value: 'naturalGas', label: 'Natural gas heating', icon: '🔥' },
      { value: 'fossilFuels', label: 'Other fossil fuels', icon: '⛽' },
    ],
  },
  {
    id: 'waterConservation',
    category: 'Home Energy',
    question: 'Do you take measures to conserve water at home?',
    info: 'Water conservation helps reduce energy used for heating and pumping water.',
    type: 'single',
    options: [
      { value: 'extensive', label: 'Yes, extensively (low-flow devices, very short showers)', icon: '💧' },
      { value: 'moderate', label: 'Somewhat (conscious of water use)', icon: '💧' },
      { value: 'minimal', label: 'Not really (long showers, no special effort)', icon: '💧' },
    ],
  },

  // DIET & FOOD
  {
    id: 'diet',
    category: 'Diet & Food',
    question: 'Which diet best describes you?',
    info: 'Livestock farming contributes 14-15% of global GHG emissions. 1 kg of beef uses ~15,000 liters of water.',
    type: 'single',
    options: [
      { value: 'vegan', label: 'Vegan (no animal products)', icon: '🥗' },
      { value: 'vegetarian', label: 'Vegetarian (no meat, but dairy/eggs)', icon: '🥚' },
      { value: 'mixedLowMeat', label: 'Mixed diet with low meat consumption', icon: '🍽️' },
      { value: 'meatHeavy', label: 'Meat-heavy diet (meat daily)', icon: '🥩' },
    ],
  },
  {
    id: 'localFood',
    category: 'Diet & Food',
    question: 'How often do you buy locally grown or organic food?',
    info: 'Local food reduces transport emissions and supports sustainable farming.',
    type: 'single',
    options: [
      { value: 'usually', label: 'Usually', icon: '🌾' },
      { value: 'sometimes', label: 'Sometimes', icon: '🌾' },
      { value: 'rarely', label: 'Rarely / Not a priority', icon: '🌾' },
    ],
  },
  {
    id: 'foodWaste',
    category: 'Diet & Food',
    question: 'How much food waste do you generate?',
    info: 'Food waste in landfills produces methane, a potent greenhouse gas.',
    type: 'single',
    options: [
      { value: 'veryLittle', label: 'Very little (compost leftovers, plan meals)', icon: '♻️' },
      { value: 'some', label: 'Some (throw away food occasionally)', icon: '🗑️' },
      { value: 'alot', label: 'A lot (frequently discard expired food)', icon: '🗑️' },
    ],
  },

  // FASHION & SHOPPING
  {
    id: 'clothingFrequency',
    category: 'Fashion & Shopping',
    question: 'How often do you purchase new clothes or shoes?',
    info: 'The fashion industry uses 93 billion cubic meters of water yearly. One cotton shirt requires ~2,700 liters.',
    type: 'single',
    options: [
      { value: 'rarely', label: 'Rarely (a few times a year, only when needed)', icon: '👕' },
      { value: 'sometimes', label: 'Sometimes (about 1-2 items per month)', icon: '👕' },
      { value: 'frequently', label: 'Frequently (multiple items per month)', icon: '👕' },
    ],
  },
  {
    id: 'clothingDisposal',
    category: 'Fashion & Shopping',
    question: 'What do you do with clothes you no longer use?',
    info: '85% of textiles end up in dumps each year.',
    type: 'single',
    options: [
      { value: 'donate', label: 'Donate or sell them', icon: '🤝' },
      { value: 'repurpose', label: 'Repurpose or recycle materials', icon: '♻️' },
      { value: 'trash', label: 'Throw them in the trash', icon: '🗑️' },
    ],
  },
  {
    id: 'fashionType',
    category: 'Fashion & Shopping',
    question: 'Do you prefer sustainable/second-hand or fast fashion?',
    info: 'Fast fashion is responsible for ~10% of global carbon emissions.',
    type: 'single',
    options: [
      { value: 'sustainable', label: 'Yes, mostly sustainable/second-hand', icon: '♻️' },
      { value: 'mixed', label: 'Mix of both', icon: '🛍️' },
      { value: 'fastFashion', label: 'Mostly fast-fashion/cheap brands', icon: '🛍️' },
    ],
  },

  // WASTE & PLASTIC
  {
    id: 'recycling',
    category: 'Waste & Plastic',
    question: 'Do you recycle household waste regularly?',
    info: 'Recycling can significantly reduce environmental impact.',
    type: 'single',
    options: [
      { value: 'always', label: 'Yes, always recycle everything possible', icon: '♻️' },
      { value: 'sometimes', label: 'I recycle sometimes, but not consistently', icon: '♻️' },
      { value: 'rarely', label: 'Rarely or never recycle', icon: '🗑️' },
    ],
  },
  {
    id: 'singleUsePlastic',
    category: 'Waste & Plastic',
    question: 'How often do you use single-use plastics?',
    info: 'Global plastic waste averages 28 kg per person per year.',
    type: 'single',
    options: [
      { value: 'hardlyEver', label: 'Hardly ever (carry reusable bottles/bags)', icon: '♻️' },
      { value: 'sometimes', label: 'Sometimes (use when convenient)', icon: '🥤' },
      { value: 'often', label: 'Often (regularly rely on single-use plastics)', icon: '🥤' },
    ],
  },

  // GENERAL CONSUMPTION
  {
    id: 'electronics',
    category: 'General Consumption',
    question: 'How often do you upgrade personal electronics?',
    info: 'E-waste is growing rapidly. Using devices longer reduces waste.',
    type: 'single',
    options: [
      { value: 'broken', label: 'Only when old one is broken (rarely)', icon: '📱' },
      { value: 'fewYears', label: 'Every few years', icon: '📱' },
      { value: 'yearly', label: 'Every year (always have latest)', icon: '📱' },
    ],
  },
  {
    id: 'ecoProducts',
    category: 'General Consumption',
    question: 'Do you purchase eco-friendly products?',
    info: 'Eco-friendly products often have lower environmental impact.',
    type: 'single',
    options: [
      { value: 'usually', label: 'Usually', icon: '🌱' },
      { value: 'sometimes', label: 'Sometimes', icon: '🌱' },
      { value: 'rarely', label: 'Rarely / Not concerned', icon: '🌱' },
    ],
  },
  {
    id: 'householdSize',
    category: 'General Consumption',
    question: 'What is the size of your household?',
    info: 'Shared resources in larger households can reduce per-person impact.',
    type: 'single',
    options: [
      { value: '1', label: 'Single person', icon: '🏠' },
      { value: '2-3', label: '2-3 people', icon: '🏠' },
      { value: '4+', label: '4+ people', icon: '🏠' },
    ],
  },
];

// ============================================================================
// COMPANY QUESTIONS
// ============================================================================

export const COMPANY_QUESTIONS = [
  // ENVIRONMENTAL
  {
    id: 'carbonMeasurement',
    category: 'Environmental',
    question: 'Does your company measure its carbon footprint regularly?',
    info: 'Carbon accounting is the foundation of climate action.',
    type: 'single',
    points: { no: 0, planning: 5, annual: 10, verified: 15 },
    options: [
      { value: 'no', label: 'No, not yet', icon: '❌' },
      { value: 'planning', label: 'In progress (starting to track)', icon: '📊' },
      { value: 'annual', label: 'Yes, we measure annually', icon: '✅' },
      { value: 'verified', label: 'Yes, with third-party verification', icon: '🏆' },
    ],
  },
  {
    id: 'renewableEnergy',
    category: 'Environmental',
    question: "What percentage of your company's energy comes from renewable sources?",
    info: 'Renewable energy is key to reducing operational emissions.',
    type: 'single',
    points: { '0': 0, '1-20': 3, '21-50': 7, '51-99': 12, '100': 15 },
    options: [
      { value: '0', label: '0% (none)', icon: '⛽' },
      { value: '1-20', label: '1-20%', icon: '🔋' },
      { value: '21-50', label: '21-50%', icon: '🔋' },
      { value: '51-99', label: '51-99%', icon: '☀️' },
      { value: '100', label: '100% renewable', icon: '💚' },
    ],
  },
  {
    id: 'emissionTargets',
    category: 'Environmental',
    question: 'Has the company set targets to reduce carbon emissions?',
    info: 'Public targets drive accountability and progress.',
    type: 'single',
    points: { no: 0, considering: 3, targets: 10, netZero: 15 },
    options: [
      { value: 'no', label: 'No targets', icon: '❌' },
      { value: 'considering', label: 'Considering targets', icon: '🤔' },
      { value: 'targets', label: 'Yes, reduction targets set', icon: '🎯' },
      { value: 'netZero', label: 'Committed to net-zero', icon: '🌍' },
    ],
  },
  {
    id: 'wasteManagement',
    category: 'Environmental',
    question: 'How does the company manage waste and recycling?',
    info: 'Comprehensive waste management reduces environmental impact.',
    type: 'single',
    points: { none: 0, basic: 5, comprehensive: 10, zeroWaste: 15 },
    options: [
      { value: 'none', label: 'No formal policy', icon: '🗑️' },
      { value: 'basic', label: 'Basic recycling program', icon: '♻️' },
      { value: 'comprehensive', label: 'Comprehensive waste management', icon: '✅' },
      { value: 'zeroWaste', label: 'Zero-waste goal in place', icon: '🏆' },
    ],
  },
  {
    id: 'waterManagement',
    category: 'Environmental',
    question: 'Does the company track and minimize water usage?',
    info: 'Water stewardship is crucial for environmental sustainability.',
    type: 'single',
    points: { no: 0, some: 5, detailed: 12 },
    options: [
      { value: 'no', label: 'Not specifically', icon: '❌' },
      { value: 'some', label: 'Some measures (basic monitoring)', icon: '💧' },
      { value: 'detailed', label: 'Yes, detailed monitoring and reduction', icon: '✅' },
    ],
  },
  {
    id: 'sustainableSourcing',
    category: 'Environmental',
    question: 'Do you have a sustainable sourcing policy?',
    info: 'Sustainable sourcing reduces supply chain environmental impact.',
    type: 'single',
    points: { no: 0, partial: 5, most: 10, all: 15 },
    options: [
      { value: 'no', label: 'No', icon: '❌' },
      { value: 'partial', label: 'Partial (for some materials)', icon: '📦' },
      { value: 'most', label: 'Yes for most materials', icon: '✅' },
      { value: 'all', label: 'Yes, all materials sustainably sourced', icon: '🏆' },
    ],
  },

  // SOCIAL
  {
    id: 'laborPractices',
    category: 'Social',
    question: 'Does your company ensure fair labor practices across operations?',
    info: 'Fair labor is fundamental to social responsibility.',
    type: 'single',
    points: { no: 0, progress: 5, strong: 10, audited: 15 },
    options: [
      { value: 'no', label: 'Not formally', icon: '❌' },
      { value: 'progress', label: 'In progress (basic compliance)', icon: '📋' },
      { value: 'strong', label: 'Yes, strong policies in place', icon: '✅' },
      { value: 'audited', label: 'Yes, with supply chain audits', icon: '🏆' },
    ],
  },
  {
    id: 'diversity',
    category: 'Social',
    question: 'What diversity and inclusion measures are in place?',
    info: 'D&I initiatives create more equitable and innovative workplaces.',
    type: 'single',
    points: { none: 0, basic: 4, formal: 10, core: 15 },
    options: [
      { value: 'none', label: 'None specific', icon: '❌' },
      { value: 'basic', label: 'Basic awareness (some training)', icon: '📚' },
      { value: 'formal', label: 'Formal policies and hiring goals', icon: '✅' },
      { value: 'core', label: 'D&I is core value with measurable outcomes', icon: '🏆' },
    ],
  },
  {
    id: 'communityEngagement',
    category: 'Social',
    question: 'Does the company engage in community outreach?',
    info: 'Community engagement strengthens social license to operate.',
    type: 'single',
    points: { no: 0, occasional: 4, program: 10, significant: 15 },
    options: [
      { value: 'no', label: 'Not really', icon: '❌' },
      { value: 'occasional', label: 'Occasionally (ad-hoc donations)', icon: '🤝' },
      { value: 'program', label: 'Yes, we have a community program', icon: '✅' },
      { value: 'significant', label: 'Significant community initiatives', icon: '🏆' },
    ],
  },
  {
    id: 'employeeWellbeing',
    category: 'Social',
    question: 'How is employee well-being supported?',
    info: 'Employee well-being drives engagement and retention.',
    type: 'single',
    points: { none: 0, some: 5, robust: 12 },
    options: [
      { value: 'none', label: 'No specific data or programs', icon: '❌' },
      { value: 'some', label: 'Some wellness initiatives', icon: '💪' },
      { value: 'robust', label: 'Robust wellness and development programs', icon: '🏆' },
    ],
  },

  // GOVERNANCE
  {
    id: 'strategyIntegration',
    category: 'Governance',
    question: 'Is sustainability integrated into your core strategy?',
    info: 'Strategic integration ensures sustainability is prioritized.',
    type: 'single',
    points: { no: 0, exploring: 3, defined: 10, core: 15 },
    options: [
      { value: 'no', label: 'Not explicitly', icon: '❌' },
      { value: 'exploring', label: "We're exploring it", icon: '🔍' },
      { value: 'defined', label: 'Yes, with defined objectives', icon: '✅' },
      { value: 'core', label: "Core part of mission and business model", icon: '🏆' },
    ],
  },
  {
    id: 'esgReporting',
    category: 'Governance',
    question: 'Does your company publish a Sustainability/ESG Report?',
    info: '90% of S&P 500 companies release ESG reports.',
    type: 'single',
    points: { no: 0, some: 5, annual: 12, standards: 15 },
    options: [
      { value: 'no', label: 'No report yet', icon: '❌' },
      { value: 'some', label: 'Some ESG info communicated', icon: '📄' },
      { value: 'annual', label: 'Yes, annual sustainability report', icon: '✅' },
      { value: 'standards', label: 'Yes, following international standards (GRI, SASB)', icon: '🏆' },
    ],
  },
  {
    id: 'sdgAlignment',
    category: 'Governance',
    question: 'Which SDGs does your company actively contribute to?',
    info: 'SDG alignment demonstrates commitment to global goals.',
    type: 'multiple',
    points: { none: 0, few: 5, several: 10, many: 15 },
    options: [
      { value: 'none', label: 'None specifically', icon: '❌' },
      { value: 'sdg3', label: 'SDG 3: Good Health', icon: '🏥' },
      { value: 'sdg7', label: 'SDG 7: Clean Energy', icon: '⚡' },
      { value: 'sdg8', label: 'SDG 8: Decent Work', icon: '💼' },
      { value: 'sdg12', label: 'SDG 12: Responsible Consumption', icon: '♻️' },
      { value: 'sdg13', label: 'SDG 13: Climate Action', icon: '🌍' },
      { value: 'other', label: 'Other SDGs', icon: '🎯' },
    ],
  },
  {
    id: 'governanceStructure',
    category: 'Governance',
    question: 'Do you have a governance structure for sustainability?',
    info: 'Dedicated leadership ensures accountability.',
    type: 'single',
    points: { no: 0, informal: 4, designated: 10, board: 15 },
    options: [
      { value: 'no', label: 'No specific roles', icon: '❌' },
      { value: 'informal', label: 'Yes, an informal team', icon: '👥' },
      { value: 'designated', label: 'Designated officer or department', icon: '✅' },
      { value: 'board', label: 'Board-level oversight', icon: '🏆' },
    ],
  },
  {
    id: 'transparency',
    category: 'Governance',
    question: 'How transparent is the company about ESG performance?',
    info: 'Transparency builds trust with stakeholders.',
    type: 'single',
    points: { not: 0, somewhat: 4, mostly: 10, completely: 15 },
    options: [
      { value: 'not', label: 'Not transparent (little public info)', icon: '❌' },
      { value: 'somewhat', label: 'Somewhat (selective info)', icon: '📊' },
      { value: 'mostly', label: 'Mostly transparent (regular updates)', icon: '✅' },
      { value: 'completely', label: 'Completely transparent (full disclosure)', icon: '🏆' },
    ],
  },
  {
    id: 'certifications',
    category: 'Governance',
    question: 'Has the company received sustainability certifications?',
    info: 'Certifications validate sustainability efforts.',
    type: 'single',
    points: { none: 0, oneTwo: 5, several: 12, leader: 15 },
    options: [
      { value: 'none', label: 'None', icon: '❌' },
      { value: 'oneTwo', label: 'One or two (ISO 14001, B-Corp pending)', icon: '📜' },
      { value: 'several', label: 'Yes, several certifications', icon: '✅' },
      { value: 'leader', label: 'Industry leader with top ratings', icon: '🏆' },
    ],
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getQuestionsByCategory = (questions) => {
  const categories = {};
  questions.forEach(q => {
    if (!categories[q.category]) {
      categories[q.category] = [];
    }
    categories[q.category].push(q);
  });
  return categories;
};

export const getCategoryProgress = (questions, answers, currentCategory) => {
  const categoryQuestions = questions.filter(q => q.category === currentCategory);
  const answeredInCategory = categoryQuestions.filter(q => answers[q.id] !== undefined).length;
  return {
    total: categoryQuestions.length,
    answered: answeredInCategory,
    percentage: (answeredInCategory / categoryQuestions.length) * 100
  };
};
